import { useAppSelector } from '~/redux/hooks';

/**
 * Whether the authenticated screens should be held shut, and why.
 *
 * One hook so the gate and the modal can never disagree: the same answer
 * decides whether the screen renders, whether the modal asking for a code is
 * mounted, and whether the member is refused outright.
 *
 * 'unknown' and 'checking' answer no to isMfaDue and yes to isResolving. They
 * are a third state, not a soft no: a member holding a good session must not
 * have their screen blanked while the server is still being asked, and one who
 * does not must not be shown a screen they have not verified for. Getting this
 * wrong shows a frame of real case data on every reload before the modal
 * appears over it.
 *
 * It always resolves: useAdminAccess treats a failed check as an invalid
 * session rather than leaving the status unset, so this cannot spin forever on
 * a network error.
 *
 * ## Why ineligibility is refusal here, not a pass
 *
 * The Core web app lets a non-eligible member straight through — Admin is one
 * section of a larger app and they simply never see it. There is no larger app
 * here, so the same rule would be a bypass: `isMfaDue` would be false and every
 * staff screen would render with no verification at all.
 *
 * That is reachable. Login denies anyone whose `/admin/welfare/me` lookup
 * fails, which needs only an active `welfare_admin` record to exist. Core's
 * `check_user_can_access_welfare_admin` additionally requires `is_super_admin`
 * or at least one granted permission. So a record with everything switched off
 * signs in successfully and is reported ineligible — the two services disagree,
 * and the gap opens exactly for the member who should have least access. No
 * such record exists on dev today; nothing stops one being created.
 *
 * Refusing is the fail-closed reading and cannot deadlock. Demanding a code
 * would: the challenge endpoint applies the same eligibility rule and would
 * decline to send one, leaving them stuck at a prompt with no way through.
 *
 * @returns {Object} isResolving, isMfaDue, isCheckFailed and isIneligible — at
 *   most one is true
 */
const useAdminMfaGate = () => {
    const { status, canAccessAdmin, canAccessWelfareAdmin, isMemberLocked } = useAppSelector(
        ({ adminMfa }) => adminMfa
    );

    const isResolving = status === 'unknown' || status === 'checking';
    const isEligible = canAccessAdmin || canAccessWelfareAdmin;

    return {
        isResolving,
        isMemberLocked,
        isMfaDue: status === 'required' && isEligible,
        // The check failed rather than refused. Offered a retry instead of a
        // code prompt, because the prompt would go over the same connection.
        isCheckFailed: status === 'error',
        // A live session is never second-guessed on eligibility: the server
        // issued it, and it is the server's own answer that is in question.
        // 'error' is excluded too — an unanswered check says nothing about
        // eligibility, and reading it as "no access" would tell a member with
        // perfectly good permissions that they have none.
        isIneligible: !isResolving && status !== 'valid' && status !== 'error' && !isEligible
    };
};

export default useAdminMfaGate;
